import { defineStore, storeToRefs } from "pinia";
import { computed, watch, ref } from "vue";
import { useServices, types } from "@/lib/openapi";
import {
    AuthStatus,
    useAuthenticationService,
} from "@/composables/authentication";

export type Organization = types.Organization;

export const useOrganizationsStore = defineStore("organizations", () => {
    const isLoading = ref(false);
    const organizations = ref<Organization[]>();
    const { authStatus } = useAuthenticationService();
    const api = useServices({
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    const controller = ref(new AbortController());

    const getOrganizations = async () => {
        controller.value.abort();
        controller.value = new AbortController();

        isLoading.value = true;

        try {
            organizations.value = (
                await api.startOrganizationOrganizationMgtGetOrganizationsStartPost(
                    { signal: controller.value.signal }
                )
            ).data;
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isLoading.value = false;
        }
    };

    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus === AuthStatus.SIGNED_IN) {
                getOrganizations();
            }
        },
        { immediate: true }
    );

    return {
        getOrganizations,
        organizations,
        isLoading,
        organizationNames: computed(() =>
            (organizations.value || [])
                .map((o) => o.name)
                .sort((a, b) =>
                    a.localeCompare(b, undefined, { "sensitivity": "base" })
                )
        ),
    };
});

export const useOrganizationsManager = () =>
    storeToRefs(useOrganizationsStore());
